import React from 'react';

import { OrderShort } from '@solidgate/react-sdk';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import { useModalContext } from '~/contexts/modal/ModalContext';
import { useSnackBarContext } from '~/features/snackbar/SnackbarContext';
import BenefitsAppliedModal from '~/modules/prepaid/components/BenefitsAppliedModal';
import BenefitsDeclinedModal from '~/modules/prepaid/components/BenefitsDeclinedModal';
import NonPrepaidModal from '~/modules/prepaid/components/NonPrepaidModal';
import {
  nonPrepaidModalConfig,
  PREPAID_PURCHASE_SOURCE,
  PrepaidBenefits,
} from '~/modules/prepaid/constants';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { timeout } from '~/utils/common';

export const usePrepaid = (
  benefit: PrepaidBenefits,
): { handleOpenNonPrepaidModal: () => void } => {
  const { t } = useTranslation();
  const { handleOpenModal } = useModalContext();
  const { loadAndDispatchActualSubscriptionData } = useActions();
  const { showSnackbar } = useSnackBarContext();

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    try {
      await timeout(3000);
      const response = await Api.checkUserPrepaidBenefits({
        order_id: order.order_id || '',
      });

      if (!response || !response.benefit) {
        throw Error('not eligible to benefits');
      }

      loadAndDispatchActualSubscriptionData();

      handleOpenModal({
        onClose: () => {},
        disableBackdropClick: true,
        component: ({ onClose }) => (
          <BenefitsAppliedModal benefit={response.benefit} onSubmit={onClose} />
        ),
      });
    } catch (e: any) {
      const errorMessage = e.error || e.message || 'Error :(';

      if (errorMessage === 'benefits already added') {
        showSnackbar(
          t(
            'notification:benefits_already_added',
            'Benefits Already Added. Please reload your page',
          ),
          'success',
        );

        return;
      }

      handleOpenModal({
        disableBackdropClick: true,
        component: () => (
          <BenefitsDeclinedModal
            benefit={benefit}
            onSubmit={() => {
              setTimeout(() => {
                handleOpenNonPrepaidModal();
              }, 0);
            }}
          />
        ),
      });
    }
  };

  const handleOpenNonPrepaidModal = async (): Promise<void> => {
    const eventProperties = {
      payment_update_source: 'prepaid_promo',
      promo_type: benefit,
    };

    handleOpenModal({
      disableBackdropClick: true,
      onClose: () => {
        analytics.trackEvent(
          'platform checkout - close payment update',
          eventProperties,
        );
      },
      ...nonPrepaidModalConfig,
      component: () => (
        <NonPrepaidModal
          benefit={benefit}
          eventProperties={{
            payment_update_source: PREPAID_PURCHASE_SOURCE,
            promo_type: benefit,
          }}
          onSuccess={async (order) => await handlePaymentSuccess(order)}
        />
      ),
    });
  };

  return { handleOpenNonPrepaidModal };
};
